import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Package, Hammer, Mail } from "lucide-react";
import { api } from "../lib/api";
import { formatINR } from "../lib/products";
import { OrderStatusControl } from "../components/OrderStatusControl";

const TABS = [
  { id: "orders", label: "Orders", icon: Package },
  { id: "customs", label: "Custom Builds", icon: Hammer },
  { id: "messages", label: "Messages", icon: Mail },
];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const StatusPill = ({ status }) => (
  <span
    className={`af-mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 ${
      status === "paid"
        ? "bg-green-500/15 text-green-400 border border-green-500/30"
        : status === "failed"
        ? "bg-red-500/15 text-red-400 border border-red-500/30"
        : "bg-white/5 text-zinc-400 border border-white/10"
    }`}
  >
    {status}
  </span>
);

export default function AdminPage() {
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState(null);
  const [customs, setCustoms] = useState(null);
  const [messages, setMessages] = useState(null);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        await api.get("/auth/me");
        setChecking(false);
        const [o, c, m] = await Promise.all([
          api.get("/admin/orders"),
          api.get("/admin/custom-requests"),
          api.get("/admin/contacts"),
        ]);
        setOrders(o.data.orders || []);
        setCustoms(c.data || []);
        setMessages(m.data || []);
      } catch {
        navigate("/admin/login");
      }
    })();
  }, [navigate]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      navigate("/admin/login");
    }
  };

  if (checking)
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-zinc-500 af-mono text-xs uppercase tracking-[0.25em]">
        // Checking credentials…
      </div>
    );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 px-6 md:px-12 py-5 flex items-center justify-between">
        <div>
          <p className="af-mono text-[10px] uppercase tracking-[0.25em] text-[#FF2800]">
            // Race Control
          </p>
          <h1 className="af-display text-2xl font-black uppercase tracking-tight">
            Apex Designs Admin
          </h1>
        </div>
        <button
          type="button"
          onClick={logout}
          className="inline-flex items-center gap-2 border border-white/15 px-3 py-2 hover:border-white/40 transition-colors af-mono text-[11px] uppercase tracking-widest"
          data-testid="admin-logout-button"
        >
          <LogOut size={14} /> Logout
        </button>
      </header>

      <div className="px-6 md:px-12 py-6">
        <div className="flex gap-2 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 af-mono text-[11px] uppercase tracking-widest border transition-colors ${
                tab === t.id
                  ? "bg-[#FF2800] border-[#FF2800] text-white"
                  : "border-white/15 text-zinc-400 hover:border-white/40"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              <t.icon size={13} />
              {t.label}
              <span className="opacity-70">
                {t.id === "orders"
                  ? (orders || []).length
                  : t.id === "customs"
                  ? (customs || []).length
                  : (messages || []).length}
              </span>
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-4">
          {tab === "orders" &&
            (orders === null ? (
              <p className="text-zinc-500 text-sm">Loading…</p>
            ) : orders.length === 0 ? (
              <p className="text-zinc-500 text-sm" data-testid="admin-no-orders">
                No orders yet. They'll show up here the moment someone pays. 🏁
              </p>
            ) : (
              orders.map((o) => (
                <div
                  key={o.session_id}
                  className="border border-white/10 bg-[#0E0E0E] p-5 grid md:grid-cols-12 gap-4"
                  data-testid={`admin-order-${o.session_id}`}
                >
                  <div className="md:col-span-3">
                    <p className="af-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                      {fmtDate(o.created_at)}
                    </p>
                    <p className="af-display text-xl font-bold mt-1">
                      {formatINR(o.amount)}
                    </p>
                    <div className="mt-2">
                      <StatusPill status={o.payment_status} />
                    </div>
                    <p className="af-mono text-[10px] text-zinc-600 mt-2 break-all">
                      {o.session_id}
                    </p>
                  </div>
                  <div className="md:col-span-4">
                    <p className="af-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
                      Items
                    </p>
                    {(o.items || []).map((it, i) => (
                      <p key={i} className="text-sm text-zinc-300">
                        {it.name} × {it.qty}
                        {it.options_label ? (
                          <span className="text-zinc-500"> · {it.options_label}</span>
                        ) : null}
                      </p>
                    ))}
                  </div>
                  <div className="md:col-span-5">
                    <p className="af-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
                      Ship to
                    </p>
                    {o.shipping ? (
                      <div className="text-sm text-zinc-300 leading-relaxed" data-testid="admin-order-shipping">
                        <p className="font-semibold text-white">{o.shipping.name}</p>
                        <p>{o.shipping.address}</p>
                        <p>
                          {o.shipping.city}, {o.shipping.state} — {o.shipping.pincode}
                        </p>
                        <p className="text-zinc-400 af-mono text-xs mt-1">
                          {o.shipping.phone} · {o.shipping.email}
                        </p>
                      </div>
                    ) : (
                      <p className="text-zinc-600 text-sm">No address (old order)</p>
                    )}
                  </div>
                  {o.payment_status === "paid" && (
                    <div className="md:col-span-12">
                      <OrderStatusControl
                        order={o}
                        onUpdated={(sid, patch) =>
                          setOrders((prev) =>
                            prev.map((x) => (x.session_id === sid ? { ...x, ...patch } : x))
                          )
                        }
                      />
                    </div>
                  )}
                </div>
              ))
            ))}

          {tab === "customs" &&
            (customs === null ? (
              <p className="text-zinc-500 text-sm">Loading…</p>
            ) : customs.length === 0 ? (
              <p className="text-zinc-500 text-sm">No custom build requests yet.</p>
            ) : (
              customs.map((c) => (
                <div key={c.id} className="border border-white/10 bg-[#0E0E0E] p-5" data-testid={`admin-custom-${c.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <p className="font-semibold">{c.name}</p>
                    <p className="af-mono text-[10px] text-zinc-500">{fmtDate(c.created_at)}</p>
                  </div>
                  <p className="text-sm text-zinc-300 mt-1">
                    Wants: <span className="text-white">{c.car_model}</span>
                    {c.budget_inr ? ` · Budget ${formatINR(c.budget_inr)}` : ""}
                  </p>
                  {c.notes && <p className="text-sm text-zinc-400 mt-2">{c.notes}</p>}
                  <p className="af-mono text-xs text-zinc-500 mt-2">
                    {c.email}
                    {c.phone ? ` · ${c.phone}` : ""}
                  </p>
                </div>
              ))
            ))}

          {tab === "messages" &&
            (messages === null ? (
              <p className="text-zinc-500 text-sm">Loading…</p>
            ) : messages.length === 0 ? (
              <p className="text-zinc-500 text-sm">No contact messages yet.</p>
            ) : (
              messages.map((m) => (
                <div key={m.id} className="border border-white/10 bg-[#0E0E0E] p-5" data-testid={`admin-message-${m.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <p className="font-semibold">{m.name}</p>
                    <p className="af-mono text-[10px] text-zinc-500">{fmtDate(m.created_at)}</p>
                  </div>
                  <p className="text-sm text-zinc-300 mt-2">{m.message}</p>
                  <p className="af-mono text-xs text-zinc-500 mt-2">{m.email}</p>
                </div>
              ))
            ))}
        </div>
      </div>
    </div>
  );
}
